import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import clsx from 'clsx'
import {
  formatShortDate,
  hasQuickAddParse,
  minutesToInputTime,
  parseQuickAdd,
  quickAddTitle,
  type ISODate,
} from '@app/shared'

import { Calendar } from '../icons'
import { Plus } from './icons'
import { ParseChip } from './ParseChip'
import { PhoneMayMissNote } from './PhoneMayMissNote'
import { UNFILED } from './derive'
import { IN_ONE_HOUR_LABEL, REPEAT_OPTIONS, TIME_PRESETS, inOneHour, repeatHint } from './presets'

/**
 * QUICK ADD — the composer in the header band. One field, one optional day, no required anything.
 *
 * THE DAY IS SET HERE NOW (owner, 2026-08-28). Until PR #46 the ONLY way to date something was to
 * add it undated and then reopen it from the row menu — two acts for one thought, on the screen
 * whose entire job is that thought. The calendar button opens a real `<input type="date">` in the
 * bar; leaving it shut adds exactly what it always added, an undated row in the unfiled list.
 *
 * SHUT MEANS UNDATED AND UNTIMED, and that is enforced rather than assumed: collapsing the
 * picker CLEARS the day and the hour together. A hidden field that is still quietly attaching a
 * day or an hour to everything typed after it is the app knowing something it never showed
 * anybody.
 *
 * AND NOW THE WORDS SET IT TOO (PR #52). The mockup's placeholder promised `natural language:
 * "email advisor friday"` and for months this file said, in as many words, that no such parser
 * existed. It exists now — `parseQuickAdd` in packages/shared, a pure table-driven grammar with
 * no model call anywhere near it — and it is wired under three rules that are the whole reason it
 * is allowed on a screen that refuses to fake knowledge:
 *
 *   1. IT NEVER TOUCHES THE FIELD. The draft the owner typed is the draft that stays on screen,
 *      character for character. The parse fills in the PICKERS beside it, which are separate
 *      controls he can see and correct. Rewriting an input mid-keystroke would also detonate the
 *      browser's own undo stack, which is the one contract no gate here can check.
 *   2. IT SHOWS ITS WORK BEFORE THE SUBMIT. The chip under the bar prints the resolved values —
 *      "→ Fri Sep 4 · 3:00 PM · #School" — so what will be saved is legible while there is still
 *      time to change it. ✕ rejects the reading and stands the parser down for the rest of this
 *      line.
 *   3. A TOUCHED PICKER WINS, permanently, for that draft. The moment a human sets the date or the
 *      time by hand, the parser stops proposing one: two authorities on one field is how a screen
 *      starts overwriting people.
 *
 * THE TIME IS NOW STORED, AS OF THE SAME DAY (owner, 2026-08-28, second report that afternoon:
 * "the reminder section only has a way to change the date... it doesn't allow you to select the
 * time"). Until this landed, `due` was a calendar day and nothing here wrote a time of day: the
 * one timestamp a task carries is `reminderAt`, which is not a clock on a task but the flag that
 * decides whether a row IS a reminder at all — the Worker's reminders read is
 * `filters: { or: '(reminder_at.not.is.null,due.is.null)' }` (apps/worker/src/routes/tasks.ts,
 * remindersRoute) — so writing `reminderAt` from here to mean "3pm" used to silently move the row
 * off this board and onto the Today card's Reminders card (RemindersCard,
 * components/today/ContextCards.tsx). That was the whole argument for DATE ONLY, NO TIME OF DAY,
 * and a bare `3pm` in a draft was read, shown on the chip, and then left in the title, unsaved.
 *
 * The orchestrator put the actual consequence to the owner directly, rather than quietly building
 * around it: yes, a row dated (and timed) from this bar now also becomes a TIMED reminder and
 * will surface on Today — and he chose that (2026-08-28). So the objection above is answered, not
 * deleted: a time box now sits beside the date box, opened and closed together, and a time the
 * PARSER reads is now saved rather than left in the words — using the exact defaulting
 * RemindersCard already proved out. `DEFAULT_REMINDER_TIME` (09:00) is the fallback when a day is
 * set and the hour is not, and the hint line says so before the row is written, not after (see
 * the `goes to …` span below). `packages/shared/src/quickadd.ts` itself is unchanged: it already
 * read `minutes` correctly and a bare `3:30` still stays in the title — nothing about the grammar
 * needed to change, only what this composer DOES with what it hands back.
 *
 * A TIME WITH NO DAY IS IGNORED, not disallowed: both boxes can be open at once (a day-gated time
 * box would be one more click for no reason RemindersCard doesn't already have), but the write
 * only ever carries a `reminderAt` when `due` is also set. A "3pm" parsed with no day behind it —
 * or typed into the time box before a day is picked — stays exactly where it was: in the chip's
 * note, and in the title if nothing ever claims it.
 *
 * THE UNDO CONTRACT. This is a controlled input, deliberately: it holds one line, it is cleared
 * only by a submit the user asked for, and nothing autosaves into it — so the browser's native
 * undo stack inside the field is never reset out from under a keystroke. The date, the time, and
 * the parse each live in their own SEPARATE controls, so nothing about any of them can rewrite
 * the text field mid-keystroke. The uncontrolled treatment the journal composer needs
 * (JournalEntryCard) is for a field an autosave writes back into, which this one is not.
 */
/** Stable empty default: a fresh `[]` per render would defeat the parse memo below. */
const NO_LISTS: readonly string[] = []

/**
 * The hour a reminder fires when a day is set (by hand or by the parser) and no hour came with
 * it. A DEFAULT, not a guess: it is the same constant RemindersCard falls back to
 * (components/today/ContextCards.tsx), so the two composers can never disagree about what "blank"
 * means, and it is printed under the fields before the row is written, so nothing about the saved
 * timestamp is news afterwards.
 */
const DEFAULT_REMINDER_TIME = '09:00'
const DEFAULT_REMINDER_LABEL = '9:00 AM'

export function QuickAdd({
  onAdd,
  today,
  lists = NO_LISTS,
  disabledReason,
}: {
  onAdd: (input: {
    title: string
    due: ISODate | null
    list: string | null
    reminderAt: string | null
    /** One of `REPEAT_OPTIONS`' rules, or null. Never set without a `due` — see the submit. */
    rrule: string | null
  }) => void
  /**
   * The board's own today, for resolving "friday" forward. Undefined until the clock read
   * returns — and while it is undefined NOTHING relative is parsed, because a parser that
   * guesses at what day it is would be the app inventing the one fact it is missing.
   */
  today?: ISODate
  /** Existing list names. A `#tag` matching one files the row; an unknown tag stays in the title. */
  lists?: readonly string[]
  /**
   * Set when adding cannot work right now, carrying the reason in its own words. Since the
   * tasks backend landed that means the board's READ failed — if nothing could be read, nothing
   * can be written either, and the field says so instead of swallowing a submit.
   */
  disabledReason?: string
}) {
  const [draft, setDraft] = useState('')
  const [manualDue, setManualDue] = useState('')
  const [manualTime, setManualTime] = useState('')
  const [dating, setDating] = useState(false)
  /** The date picker has been set by hand for this draft — rule 3 above. */
  const [dateTouched, setDateTouched] = useState(false)
  /** The time picker has been set by hand for this draft — the same rule, the other field. */
  const [timeTouched, setTimeTouched] = useState(false)
  /** ✕ was pressed for this draft: the words stand as typed until the line is sent. */
  const [rejected, setRejected] = useState(false)
  /** The repeat picked for this draft — an RRULE string, or '' for a one-off. */
  const [repeat, setRepeat] = useState('')
  const dateRef = useRef<HTMLInputElement>(null)
  const blocked = disabledReason !== undefined

  const parse = useMemo(
    () => (today ? parseQuickAdd(draft, { today, lists }) : null),
    [draft, today, lists],
  )
  const reading = parse && !rejected && hasQuickAddParse(parse) ? parse : null
  /** The parse only speaks for a field while no hand has been laid on that field's own picker. */
  const proposedDue = dateTouched ? null : (reading?.due ?? null)
  const proposedMinutes = timeTouched ? null : (reading?.minutes ?? null)
  const showPicker = dating || proposedDue !== null || proposedMinutes !== null
  const due = dateTouched ? manualDue : (proposedDue ?? manualDue)
  const time =
    timeTouched || proposedMinutes === null ? manualTime : minutesToInputTime(proposedMinutes)

  // Opening the picker BY HAND puts the cursor in it: the click that opened it was the whole
  // request. A picker that opened itself because a date was recognised does not steal focus —
  // the owner is still typing his sentence.
  useEffect(() => {
    if (dating) dateRef.current?.focus()
  }, [dating])

  function reset() {
    setDraft('')
    setManualDue('')
    setManualTime('')
    setDating(false)
    setDateTouched(false)
    setTimeTouched(false)
    setRejected(false)
    setRepeat('')
  }

  /**
   * A PRESET IS A HAND ON BOTH PICKERS. It sets the hour, and — because an hour with no day is
   * stored nowhere (see the header) — it sets the day too when none is set yet: the board's own
   * `today` for the clock presets, the computed day for "1 hour from now". Both fields count as
   * touched from then on, so the words cannot quietly move what a chip just set.
   */
  function applyPreset(hhmm: string, day: ISODate | null) {
    setDating(true)
    setTimeTouched(true)
    setManualTime(hhmm)
    if (day !== null && !due) {
      setDateTouched(true)
      setManualDue(day)
    } else if (!dateTouched && due) {
      // The day came from the words; keep it, but own it, so the chip's reading is stable.
      setDateTouched(true)
      setManualDue(due)
    }
  }

  /** A repeat needs a day to count from; picking one on an undated draft dates it today. */
  function applyRepeat(rrule: string) {
    setRepeat(rrule)
    if (rrule && !due && today) {
      setDating(true)
      setDateTouched(true)
      setManualDue(today)
    }
  }

  const repeatWord = due && repeat ? repeatHint(repeat) : null

  /** The instant this draft would ring at — the submit's own arithmetic, for the note below. */
  const pendingReminderAt = (() => {
    if (!due) return null
    const at = new Date(`${due}T${time || DEFAULT_REMINDER_TIME}`)
    return Number.isNaN(at.getTime()) ? null : at.toISOString()
  })()

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        // A WORD IS ONLY TAKEN OUT OF THE TITLE IF IT LANDED SOMEWHERE. The day goes when the
        // parse is what supplied it; the time goes only when there is also a day for it to hang
        // on, since `reminderAt` is built from both — a "3pm" with no day is stored nowhere, so
        // it stays in his sentence instead of being deleted into silence. The #list goes whenever
        // the parse found one, because a tag is filed the moment it is recognised.
        const keptDate = proposedDue !== null
        const keptTime = proposedMinutes !== null && due !== ''
        const title = (
          reading
            ? quickAddTitle(
                draft,
                reading.spans.filter(
                  (s) =>
                    s.kind === 'list' ||
                    (s.kind === 'date' && keptDate) ||
                    (s.kind === 'time' && keptTime),
                ),
              )
            : draft
        ).trim()
        if (!title || blocked) return
        // A TIME WITH NO DAY IS IGNORED — see the header comment. `at` is only ever computed
        // once `due` is set, exactly the guard RemindersCard's own `add()` uses.
        const at = due ? new Date(`${due}T${time || DEFAULT_REMINDER_TIME}`) : null
        onAdd({
          title,
          due: due ? (due as ISODate) : null,
          list: reading?.list ?? null,
          reminderAt: due && at && !Number.isNaN(at.getTime()) ? at.toISOString() : null,
          // A REPEAT WITHOUT A DAY IS DROPPED, not defaulted: the Worker advances a series from
          // `due`, and a repeat with nothing to count from would be a promise the row cannot keep.
          rrule: due && repeat ? repeat : null,
        })
        // A submit resets the WHOLE composer, day and hour included. A date or time that
        // outlived the row it was picked for would attach itself to the next unrelated thing
        // typed here.
        reset()
      }}
      title={disabledReason}
      style={{ '--mo-rest-shadow': 'var(--shadow-composer)' } as CSSProperties}
      className={clsx(
        'flex flex-none flex-col gap-[8px] rounded-panel border border-line-2 bg-surface px-4 py-[13px] shadow-composer',
        // 12d, TEXT ENTRY (motion-brief §2): hover fills, focus clears the fill and hands the
        // edge to the ring. The brief gives this field "the same, plus a slightly deeper resting
        // shadow" — `shadow-composer` already is that shadow, so nothing new is invented here.
        //
        // DISABLED IS INERT, not dimmed-and-still-reactive: when the read failed there is nothing
        // to type into, so the field drops the hover mechanic entirely rather than lighting up
        // under a pointer whose click it will refuse.
        blocked ? 'opacity-70' : 'mo-field',
      )}
    >
      <div className="flex items-center gap-3">
        <Plus size={17} className="flex-none text-muted" />
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          disabled={blocked}
          placeholder="Add a task…"
          aria-label="Add a task"
          className="min-w-0 flex-1 border-0 bg-transparent p-0 text-[15px] leading-none text-ink outline-none placeholder:text-microcopy disabled:cursor-not-allowed"
        />

        {showPicker && (
          <>
            <input
              ref={dateRef}
              type="date"
              value={due}
              onChange={(e) => {
                // A hand on the picker takes the field over for good, for this draft.
                setDateTouched(true)
                setDating(true)
                setManualDue(e.currentTarget.value)
              }}
              disabled={blocked}
              aria-label="Due date"
              className="flex-none rounded-context border border-line bg-surface px-[9px] py-[5px] text-[12px] font-medium leading-none text-ink-2"
            />
            {/* Beside the date box, not gated behind it: it opens the moment either a day or an
                hour is on the table, from a click or from the words, so what will be saved is
                never hiding behind one more click. */}
            <input
              type="time"
              value={time}
              onChange={(e) => {
                setTimeTouched(true)
                setDating(true)
                setManualTime(e.currentTarget.value)
              }}
              disabled={blocked}
              aria-label="Remind me at"
              className="flex-none rounded-context border border-line bg-surface px-[9px] py-[5px] text-[12px] font-medium leading-none text-ink-2"
            />
          </>
        )}

        <div className="flex flex-none items-center gap-2">
          <button
            type="button"
            disabled={blocked}
            onClick={() => {
              // Collapsing removes the day and the hour as well as the controls — see SHUT MEANS
              // UNDATED AND UNTIMED above. Either way this counts as setting both by hand, so a
              // recognised day or time cannot reopen the picker the owner just shut.
              setDateTouched(true)
              setTimeTouched(true)
              setManualDue('')
              setManualTime('')
              setDating(!showPicker)
            }}
            aria-pressed={showPicker}
            aria-label={showPicker ? 'Add it with no date' : 'Give it a date'}
            title={showPicker ? 'Add it with no date' : 'Give it a date'}
            style={{ '--mo-rule': 'var(--color-tasks)' } as CSSProperties}
            className={clsx(
              'mo-rule flex size-[26px] flex-none items-center justify-center rounded-button',
              'disabled:cursor-not-allowed',
              showPicker ? 'bg-fill-2 text-ink-2' : 'text-muted',
            )}
          >
            <Calendar size={15} />
          </button>
          {/* The hint names the destination it will actually use, tag included, AND says the
              consequence of a day out loud: once one is set, this row also becomes a timed
              reminder and shows on Today too — the owner must not discover that by surprise. The
              exact chosen hour never prints as raw text here (the `<input type="time">` already
              shows it, in the reader's own 12-hour locale); only the stated DEFAULT does, because
              that one is otherwise invisible until the row lands. */}
          <span className="whitespace-nowrap text-[11px] font-normal leading-none text-faintest-2">
            {blocked
              ? 'unavailable right now'
              : `goes to ${reading?.list ?? UNFILED} · ${
                  due
                    ? `due ${formatShortDate(due as ISODate)} · ${
                        time ? 'also shows on Today' : `reminds ${DEFAULT_REMINDER_LABEL}, also on Today`
                      }${repeatWord ? ` · ${repeatWord}` : ''}`
                    : showPicker
                      ? 'pick a day, or leave it blank'
                      : reading?.list
                        ? 'from the tag you typed'
                        : 'list from the row menu'
                }`}
          </span>
          <kbd className="rounded-keycap bg-fill-2 px-2 py-[3px] text-[10px] font-semibold leading-[1.6] text-muted">
            ⏎
          </kbd>
        </div>
      </div>

      {showPicker && !blocked && (
        // THE PRESETS ROW (owner, 2026-09-14). Four chips and a repeat, under the field, only
        // while the date is on the table — a closed picker is an undated, untimed, one-off row
        // and shows nothing that could suggest otherwise. The chips never replace the exact
        // pickers above; they fill them.
        <div className="flex flex-wrap items-center gap-[6px] pl-[29px]">
          <PresetChip
            active={false}
            onClick={() => {
              const at = inOneHour()
              applyPreset(at.hhmm, at.date)
            }}
          >
            {IN_ONE_HOUR_LABEL}
          </PresetChip>
          {TIME_PRESETS.map((p) => (
            <PresetChip
              key={p.hhmm}
              active={time === p.hhmm}
              onClick={() => applyPreset(p.hhmm, today ?? null)}
            >
              {p.label}
            </PresetChip>
          ))}
          <label className="ml-1 flex items-center gap-[6px] text-[11px] font-medium leading-none text-ink-3">
            Repeat
            <select
              value={repeat}
              onChange={(e) => applyRepeat(e.currentTarget.value)}
              aria-label="Repeat"
              className="rounded-context border border-line bg-surface px-[7px] py-[4px] text-[11.5px] font-medium leading-none text-ink-2"
            >
              {REPEAT_OPTIONS.map((o) => (
                <option key={o.label} value={o.rrule ?? ''}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      {/* Under 15 minutes away, outside the phone app: the phone may not hear of it in time. */}
      {!blocked && <PhoneMayMissNote reminderAt={pendingReminderAt} className="pl-[29px] text-[11px]" />}

      {reading && !blocked && (
        <ParseChip
          // Only what the PARSER contributed. Once a picker is touched the parse has no say in
          // that field, and a chip still reciting it would be claiming an authority it just lost.
          due={proposedDue}
          minutes={proposedMinutes}
          list={reading.list}
          // A TIME IS NOW SAVED, NOT LEFT IN THE WORDS (owner, 2026-08-28) — see the header
          // comment. The chip says which is true for THIS draft: saved, once a day backs it, or
          // still homeless when it does not.
          note={
            proposedMinutes !== null
              ? due
                ? 'saved as a timed reminder — also shows on Today'
                : 'a time needs a day — pick one, or it stays in the words'
              : undefined
          }
          onReject={() => setRejected(true)}
        />
      )}
    </form>
  )
}

/** One preset. `aria-pressed` says whether the hour field currently holds this chip's clock. */
function PresetChip({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{ '--mo-rule': 'var(--color-tasks)' } as CSSProperties}
      className={clsx(
        'mo-rule flex h-[24px] flex-none items-center rounded-pill border px-[9px] text-[11px] font-medium leading-none',
        active
          ? 'border-ink bg-ink text-surface'
          : 'border-line bg-surface text-ink-2 hover:bg-fill-2',
      )}
    >
      {children}
    </button>
  )
}
